/**
 * A/B Test Context (PRODUCTION)
 *
 * ⚠️ THIS IS PRODUCTION A/B TESTING ⚠️
 *
 * This module provides real A/B testing functionality for experiments.
 * For development theme switching, see: src/config/DevThemeConfig.jsx
 *
 * Features:
 * - Fetches active experiments on mount
 * - Assigns users to variants deterministically (via backend)
 * - Tracks exposures automatically
 * - Provides hooks for accessing experiment state
 * - Graceful degradation if API fails
 *
 * Usage:
 * ```jsx
 * import { useABTest } from '../hooks/useABTest';
 *
 * function MyComponent() {
 *   const { variant, isLoading } = useABTest('search_layout_v2');
 *
 *   if (isLoading) return <Loading />;
 *   if (variant === 'variant') return <NewLayout />;
 *   return <OldLayout />;
 * }
 * ```
 *
 * Part of: MVP 1.10 - A/B Testing & Experimentation Framework
 * Location: apps/web/src/contexts/ABTestContext.jsx
 * Created: 2025-10-31
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import abTestService from '../services/abTestService';

// Context
const ABTestContext = createContext(null);

/**
 * A/B Test Provider Component
 *
 * Place this high in your component tree (e.g., in App.jsx).
 * It will fetch active experiments and manage user assignments.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {string} props.userId - Current user ID (UUID)
 * @param {string} props.sessionId - Current session ID (optional)
 */
export function ABTestProvider({ children, userId, sessionId }) {
  // State
  const [experiments, setExperiments] = useState([]); // All active experiments
  const [assignments, setAssignments] = useState({}); // Map of experimentName -> variant
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exposureTracked, setExposureTracked] = useState(new Set()); // Track which experiments we've tracked exposure for

  /**
   * Fetch all active experiments on mount
   */
  useEffect(() => {
    let isMounted = true;

    async function fetchExperiments() {
      try {
        setLoading(true);
        const activeExperiments = await abTestService.getActiveExperiments();

        if (isMounted) {
          setExperiments(activeExperiments);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          console.debug('[ABTestContext] Could not fetch experiments (API may be starting):', err.message);
          setError(err.message);
          setExperiments([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchExperiments();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Get variant for a specific experiment
   *
   * This method:
   * 1. Checks if we already have an assignment cached
   * 2. If not, calls the backend to assign the user
   * 3. Caches the assignment in state
   * 4. Tracks exposure event automatically
   *
   * @param {string} experimentName - Experiment name
   * @returns {Promise<string|null>} - Variant name or null
   */
  const getVariant = useCallback(async (experimentName) => {
    // Return cached assignment if available
    // eslint-disable-next-line security/detect-object-injection -- safe: experimentName is controlled experiment identifier
    if (assignments[experimentName]) {
      // eslint-disable-next-line security/detect-object-injection -- safe: experimentName is controlled experiment identifier
      return assignments[experimentName];
    }

    // Require either userId or sessionId
    if (!userId && !sessionId) {
      console.warn('[ABTestContext] No userId or sessionId provided, cannot assign to experiment');
      return null;
    }

    try {
      // Assign user to variant (supports both authenticated and anonymous users)
      const assignment = await abTestService.assignUser(userId, experimentName, sessionId);

      // Handle no experiment case - use default variant
      if (assignment.noExperiment || assignment.isDefault) {
        console.info('[ABTestContext] No experiment available, using default:', experimentName);
        // Still cache the result to avoid repeated calls
        setAssignments(prev => ({
          ...prev,
          [experimentName]: 'control'
        }));
        return 'control';
      }

      if (assignment.variant) {
        // Cache assignment
        setAssignments(prev => ({
          ...prev,
          [experimentName]: assignment.variant
        }));

        // Track exposure if this is the first time seeing this variant
        if (!exposureTracked.has(experimentName)) {
          abTestService.trackExposure(
            experimentName,
            userId,
            assignment.variant,
            { timestamp: new Date().toISOString() },
            sessionId
          );

          setExposureTracked(prev => new Set(prev).add(experimentName));
        }

        return assignment.variant;
      }

      return null;
    } catch (err) {
      console.error(`[ABTestContext] Error getting variant for ${experimentName}:`, err);
      return null; // Graceful degradation
    }
  }, [userId, sessionId, assignments, exposureTracked]);

  /**
   * Track event for experiment
   *
   * @param {string} experimentName - Experiment name
   * @param {string} eventType - Event type (click, conversion, etc.)
   * @param {Object} properties - Optional event properties
   */
  const trackEvent = useCallback((experimentName, eventType, properties = {}) => {
    // eslint-disable-next-line security/detect-object-injection -- safe: experimentName is controlled experiment identifier
    const variant = assignments[experimentName];

    if (!variant) {
      console.warn(`[ABTestContext] Cannot track event - no variant assigned for ${experimentName}`);
      return;
    }

    if (!userId) {
      // Silent return for anonymous users - this is expected behavior
      // Anonymous users are tracked via sessionId during variant assignment
      return;
    }

    // Fire-and-forget
    abTestService.trackEvent({
      experimentName,
      userId,
      eventType,
      variant,
      properties: {
        ...properties,
        sessionId,
        timestamp: new Date().toISOString()
      }
    }).catch(err => {
      console.error(`[ABTestContext] Error tracking ${eventType} event:`, err);
    });
  }, [userId, sessionId, assignments]);

  /**
   * Track click event (convenience method)
   */
  const _trackClick = useCallback((experimentName, properties = {}) => {
    trackEvent(experimentName, 'click', properties);
  }, [trackEvent]);

  /**
   * Track conversion event (convenience method)
   */
  const trackConversion = useCallback((experimentName, properties = {}) => {
    trackEvent(experimentName, 'conversion', properties);
  }, [trackEvent]);

  /**
   * Track filter change event (convenience method for search experiments)
   */
  const trackFilterChange = useCallback((experimentName, properties = {}) => {
    trackEvent(experimentName, 'filter_change', properties);
  }, [trackEvent]);

  // Context value
  const value = {
    // State
    experiments,
    assignments,
    loading,
    error,

    // Methods
    getVariant,
    trackEvent,
    _trackClick,
    trackConversion,
    trackFilterChange,

    // User info
    userId,
    sessionId
  };

  return (
    <ABTestContext.Provider value={value}>
      {children}
    </ABTestContext.Provider>
  );
}

/**
 * Hook to access A/B Test context
 *
 * @throws {Error} - If used outside ABTestProvider
 * @returns {Object} - Context value
 */
export function useABTestContext() {
  const context = useContext(ABTestContext);

  if (!context) {
    throw new Error('useABTestContext must be used within ABTestProvider');
  }

  return context;
}

export default ABTestContext;
